import { JobRequestCustomV1 } from "@/lib/store/Realtime/message.types";
import { FieldTypeEnum } from "@/dentlab/src/pages/form-builder/types/formTypes";
import {
	CustomFormFileType,
	ToothData,
	AppointmentData,
} from "@/dentconnect/src/pages/job-request/types";
import "../fonts.css";

export interface CustomFormSubmissionTemplateProps {
	content: JobRequestCustomV1;
	formTemplate?: any;
	senderOrgName?: string;
}

export const CustomFormSubmissionTemplate: React.FC<
	CustomFormSubmissionTemplateProps
> = ({ content, formTemplate, senderOrgName }) => {
	// Helper to render a single field value
	const renderFieldValue = (
		fieldId: string,
		fieldLabel: string,
		field?: any
	) => {
		const value = content.customFormData[fieldId];
		if (!value) return null;

		const fieldType = value.fieldType;
		const fieldValue = value.fieldValue;

		// Handle different field types
		if (fieldType === FieldTypeEnum.ODONTOGRAM) {
			const toothData = fieldValue as ToothData[];

			if (!toothData || toothData.length === 0) {
				return null;
			}

			// Filter teeth that have actual values
			const teethWithValues = toothData.filter(
				(tooth) => tooth.values && Object.keys(tooth.values).length > 0
			);

			if (teethWithValues.length === 0) {
				// Just show selected teeth numbers
				return (
					<div
						key={fieldId}
						style={{
							marginBottom: "12px",
							pageBreakInside: "avoid",
						}}
					>
						<div
							style={{
								fontWeight: "600",
								marginBottom: "6px",
								fontSize: "11px",
								color: "#374151",
							}}
						>
							{fieldLabel}
						</div>
						<div
							style={{
								paddingLeft: "16px",
								backgroundColor: "#f9fafb",
								padding: "10px",
								borderRadius: "4px",
								border: "1px solid #e5e7eb",
							}}
						>
							Ausgewählte Zähne:{" "}
							{toothData.map((t) => t.toothNumber).join(", ")}
						</div>
					</div>
				);
			}

			// Show table with teeth and their values
			return (
				<div
					key={fieldId}
					style={{ marginBottom: "12px", pageBreakInside: "avoid" }}
				>
					<div
						style={{
							fontWeight: "600",
							marginBottom: "6px",
							fontSize: "11px",
							color: "#374151",
						}}
					>
						{fieldLabel}
					</div>
					<div
						style={{
							paddingLeft: "16px",
							backgroundColor: "#f9fafb",
							padding: "10px",
							borderRadius: "4px",
							border: "1px solid #e5e7eb",
						}}
					>
						{teethWithValues.map((tooth, index) => (
							<div
								key={index}
								style={{
									marginBottom: "6px",
									lineHeight: "1.6",
								}}
							>
								<span
									style={{
										fontWeight: "600",
										color: "#1f2937",
									}}
								>
									Zahn {tooth.toothNumber}:{" "}
								</span>
								<span style={{ color: "#4b5563" }}>
									{Object.entries(tooth.values).map(
										([key, val], i) => {
											const label =
												content.customFormReferences?.[
													key
												] || key;
											return (
												<span key={i}>
													{label}: {val}
													{i <
													Object.entries(tooth.values)
														.length -
														1
														? ", "
														: ""}
												</span>
											);
										}
									)}
								</span>
							</div>
						))}
					</div>
				</div>
			);
		}

		if (fieldType === FieldTypeEnum.FILE) {
			const files = fieldValue as CustomFormFileType[];
			return (
				<div
					key={fieldId}
					style={{
						marginBottom: "10px",
						display: "grid",
						gridTemplateColumns: "200px 1fr",
						gap: "12px",
						alignItems: "start",
						pageBreakInside: "avoid",
					}}
				>
					<span
						style={{
							fontWeight: "600",
							fontSize: "11px",
							color: "#374151",
						}}
					>
						{fieldLabel}:
					</span>
					<div>
						{files.map((file, index) => (
							<div
								key={index}
								style={{
									color: "#1f2937",
									fontSize: "11px",
									marginBottom: "2px",
								}}
							>
								- {file.name}
							</div>
						))}
					</div>
				</div>
			);
		}

		if (fieldType === FieldTypeEnum.APPOINTMENT) {
			const appointments = fieldValue as AppointmentData[];

			if (!appointments || appointments.length === 0) {
				return null;
			}

			const formatValue = (subFieldId: string, value: string): string => {
				const subField = field?.appointmentFields?.find(
					(f: any) => f.id === subFieldId
				);
				if (!subField || !value) return value;

				if (subField.type === "date") {
					try {
						const date = new Date(value);
						if (!isNaN(date.getTime())) {
							return date.toLocaleDateString("de-DE", {
								day: "2-digit",
								month: "2-digit",
								year: "numeric",
							});
						}
					} catch (e) {
						return value;
					}
				}

				if (subField.type === "time") {
					return value ? `${value} Uhr` : value;
				}

				return value;
			};

			return (
				<div
					key={fieldId}
					style={{ marginBottom: "12px", pageBreakInside: "avoid" }}
				>
					<div
						style={{
							fontWeight: "600",
							marginBottom: "6px",
							fontSize: "11px",
							color: "#374151",
						}}
					>
						{fieldLabel}
					</div>
					<div
						style={{
							paddingLeft: "16px",
							backgroundColor: "#f9fafb",
							padding: "10px",
							borderRadius: "4px",
							border: "1px solid #e5e7eb",
						}}
					>
						{appointments.map((appointment, index) => (
							<div
								key={appointment.appointmentId}
								style={{
									marginBottom:
										index < appointments.length - 1
											? "12px"
											: "0",
									paddingBottom:
										index < appointments.length - 1
											? "12px"
											: "0",
									borderBottom:
										index < appointments.length - 1
											? "1px solid #e5e7eb"
											: "none",
								}}
							>
								<div
									style={{
										fontWeight: "600",
										color: "#1f2937",
										marginBottom: "4px",
										fontSize: "11px",
									}}
								>
									Termin {index + 1}:
								</div>
								<div style={{ paddingLeft: "12px" }}>
									{Object.entries(appointment.values).map(
										([subFieldId, value], i) => {
											const subFieldLabel =
												content.customFormReferences?.[
													subFieldId
												] || subFieldId;
											const formattedValue = formatValue(
												subFieldId,
												value
											);
											return (
												<div
													key={subFieldId}
													style={{
														marginBottom: "3px",
														lineHeight: "1.6",
														fontSize: "11px",
													}}
												>
													<span
														style={{
															fontWeight: "600",
															color: "#374151",
														}}
													>
														{subFieldLabel}:{" "}
													</span>
													<span
														style={{
															color: "#4b5563",
														}}
													>
														{formattedValue}
													</span>
												</div>
											);
										}
									)}
								</div>
							</div>
						))}
					</div>
				</div>
			);
		}

		// Handle regular text, select, checkbox, etc.
		const displayValue = Array.isArray(fieldValue)
			? fieldValue.join(", ")
			: (fieldValue as string)?.toString() || "";

		return (
			<div
				key={fieldId}
				style={{
					marginBottom: "10px",
					display: "grid",
					gridTemplateColumns: "200px 1fr",
					gap: "12px",
					alignItems: "start",
					pageBreakInside: "avoid",
				}}
			>
				<span
					style={{
						fontWeight: "600",
						fontSize: "11px",
						color: "#374151",
					}}
				>
					{fieldLabel}:
				</span>
				<span style={{ color: "#1f2937" }}>{displayValue}</span>
			</div>
		);
	};

	// Render fields in order based on form template
	const renderFormData = () => {
		if (!content.customFormData) return null;

		// If we have the form template, render fields in order by sections
		if (formTemplate && formTemplate.definition?.sections) {
			return (
				<div>
					{formTemplate.definition.sections.map(
						(section: any, sectionIndex: number) => (
							<div
								key={sectionIndex}
								style={{
									marginBottom: "28px",
									pageBreakInside: "avoid",
								}}
							>
								{section.title && (
									<div
										style={{
											fontSize: "13px",
											fontWeight: "700",
											marginBottom: "14px",
											paddingBottom: "8px",
											borderBottom: "2px solid #d1d5db",
											color: "#111827",
											letterSpacing: "0.3px",
										}}
									>
										{section.title}
									</div>
								)}
								<div style={{ paddingLeft: "8px" }}>
									{section.fields.map((field: any) =>
										renderFieldValue(
											field.id,
											content.customFormReferences?.[
												field.id
											] ||
												field.label ||
												field.id,
											field
										)
									)}
								</div>
							</div>
						)
					)}
				</div>
			);
		}

		// Fallback to unordered display if form template not available
		return (
			<div>
				{Object.keys(content.customFormData).map((fieldId) =>
					renderFieldValue(
						fieldId,
						content.customFormReferences?.[fieldId] || fieldId
					)
				)}
			</div>
		);
	};

	return (
		<div
			style={{
				fontFamily:
					"-apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
				padding: "48px",
				fontSize: "11px",
				lineHeight: "1.7",
				color: "#1f2937",
				backgroundColor: "#ffffff",
			}}
		>
			{/* Header */}
			<div
				style={{
					borderBottom: "3px solid #111827",
					paddingBottom: "20px",
					marginBottom: "32px",
					pageBreakAfter: "avoid",
				}}
			>
				<h1
					style={{
						fontSize: "22px",
						fontWeight: "700",
						margin: "0 0 12px 0",
						color: "#111827",
						letterSpacing: "0.5px",
					}}
				>
					{content.customFormTitle || "Auftragsformular"}
				</h1>
				{senderOrgName && (
					<div
						style={{
							fontSize: "12px",
							color: "#374151",
							marginBottom: "6px",
							fontWeight: "600",
							display: "grid",
							gridTemplateColumns: "120px 1fr",
							gap: "8px",
						}}
					>
						<span>Von:</span>
						<span>{senderOrgName}</span>
					</div>
				)}
				<div
					style={{
						fontSize: "11px",
						color: "#6b7280",
						display: "grid",
						gridTemplateColumns: "120px 1fr",
						gap: "8px",
					}}
				>
					<span>Eingegangen am:</span>
					<span>
						{content.incoming_order_date
							? new Date(
									content.incoming_order_date
								).toLocaleDateString("de-DE", {
									day: "2-digit",
									month: "2-digit",
									year: "numeric",
									hour: "2-digit",
									minute: "2-digit",
								})
							: "Kein Datum"}
					</span>
				</div>
			</div>

			{/* Form Data */}
			<div style={{ marginTop: "32px" }}>{renderFormData()}</div>
		</div>
	);
};
